import React, { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FaHome, FaShieldAlt, FaUsers, FaBook, FaGraduationCap, FaStore, FaTags, FaBox, FaShoppingCart, FaClipboardList, FaImages, FaUserFriends, FaList, FaClinicMedical, FaCog, FaBiking, FaFileAlt, FaHospital, FaStethoscope, FaHeadset, FaEnvelope, FaUser, FaTicketAlt, FaCalendarAlt, FaDollarSign, FaExclamationCircle, FaSlidersH, FaTools } from 'react-icons/fa';
import { AiOutlineProduct } from 'react-icons/ai';
import { usePermissions } from '../hooks/usePermissions';
import { setUserRoles } from '../../redux/Slices/PermissionsSlice';

const SideNavMenu = ({ handleLinkClick = () => {} }) => {
  // التأكد من أن handleLinkClick معرف
  const safeHandleLinkClick = handleLinkClick || (() => {});
  const { t, i18n } = useTranslation("global");
  const { hasPermission } = usePermissions();
  const location = useLocation();
  const dispatch = useDispatch();
  
  // استخدام Redux store للحصول على بيانات المستخدم
  const user = useSelector((state) => state.auth.user);
  const { userRoles, userPermissions } = useSelector((state) => state.permissions);
  
  // fallback للبيانات من sessionStorage في حالة عدم وجود بيانات في Redux store
  const getUserData = () => {
    if (user) return { ...user, role: 'admin' };
    
    // التحقق من بيانات المدير أولاً
    const storedAdmin = sessionStorage.getItem("adminkhadamat");
    if (storedAdmin) {
      try {
        const parsed = JSON.parse(storedAdmin);
        if (parsed && parsed !== "null" && parsed.data && (parsed.data.token?.access_token || parsed.data.access_token)) {
          return { ...parsed, role: 'admin' };
        }
      } catch {
        // تجاهل الخطأ والمتابعة
      }
    }
    
    // التحقق من بيانات المتجر والصيدليات
    const storedStore = sessionStorage.getItem("storekhadamat");
    if (storedStore) {
      try {
        const parsed = JSON.parse(storedStore);
        if (parsed && (parsed.role === 'store' || parsed.role === 'pharmacy' || parsed.role === 'pharmacy_branch') && parsed.token) {
          return parsed;
        }
      } catch {
        // تجاهل الخطأ والمتابعة
      }
    }
    
    // التحقق من بيانات الفرع
    const storedBranch = sessionStorage.getItem("branchkhadamat");
    if (storedBranch) {
      try {
        const parsed = JSON.parse(storedBranch);
        if (parsed && parsed.role === 'branch') {
          return parsed;
        }
      } catch {
        // تجاهل الخطأ
      }
    }
    
    // التحقق من بيانات الدكتور
    const storedDoctor = sessionStorage.getItem("doctorKhadamat");
    if (storedDoctor) {
      try {
        const parsed = JSON.parse(storedDoctor);
        if (parsed && parsed.role === 'doctor' && parsed.token) {
          return parsed;
        }
      } catch {
        // تجاهل الخطأ
      }
    }
    
    // التحقق من بيانات العيادة
    const storedClinic = sessionStorage.getItem("clinicKhadamat");
    if (storedClinic) {
      try {
        const parsed = JSON.parse(storedClinic);
        if (parsed && parsed.role === 'clinic' && parsed.token) {
          return parsed;
        }
      } catch {
        // تجاهل الخطأ
      }
    }
    
    return null;
  };

  const userData = getUserData();
  
 

  // استرجاع الصلاحيات من session storage عند تحميل المكون
  useEffect(() => {
    // استرجع الصلاحيات من session storage فقط إذا لم تكن موجودة في Redux
    if (userRoles.length === 0 && userPermissions.length === 0) {
      const storedAdmin = sessionStorage.getItem("adminkhadamat");
      if (storedAdmin) {
        try {
          const parsed = JSON.parse(storedAdmin);
          
          // التحقق من البنية الجديدة (data.admin) أو البنية القديمة (data.roles)
          if (parsed?.data?.admin && Array.isArray(parsed.data.admin) && parsed.data.admin.length > 0) {
            // البنية الجديدة: data.admin هو array
            const roles = parsed.data.admin.map(admin => ({
              role_name_en: admin.role_name_en,
              permissions: admin.permissions || []
            }));
            dispatch(setUserRoles({ roles }));
          } else if (parsed?.data?.roles) {
            // البنية القديمة: data.roles
            dispatch(setUserRoles({ roles: parsed.data.roles }));
          }
        } catch (error) {
        }
      }
    }
  }, [dispatch, userRoles.length, userPermissions.length]);

  // إذا لم يكن هناك مستخدم، لا تعرض أي شيء
    if (!userData) {
    return null;
  }

  // الوصول للبيانات من الهيكل الصحيح للـ response
  const userInfo = userData.user || userData.data?.user || userData;
  
  // التحقق من وجود البيانات المطلوبة (بدون صلاحيات)
  if (!userInfo) {
    return null;
  }
  


  // تعريف القوائم حسب نوع المستخدم
  const getMenuItems = () => {
    const baseMenuItems = [
    {
      path: "/",
      icon: <FaHome style={{ fontSize: "20px", color: "#ffffff" }} />,
      text: t("sidenav.home")
      }
    ];

    // إذا كان المستخدم من نوع store، أضف رابط فروع المتجر
    if (userData.role === 'store') {
      baseMenuItems.push({
        path: "/store-branches",
        icon: <FaStore style={{ fontSize: "20px", color: "#ffffff" }} />,
        text: "فروع المتجر"
      });
      baseMenuItems.push({
        path: "/store/orders",
        icon: <FaShoppingCart style={{ fontSize: "20px", color: "#ffffff" }} />,
        text: "الأوردرات"
      });
    }
    if (userData.role === 'store') {
      baseMenuItems.push({
        path: "/view-products",
        icon: <AiOutlineProduct style={{ fontSize: "20px", color: "#ffffff" }} />,
        text: "المنتجات"
      });
    }

    // إذا كان المستخدم من نوع pharmacy، أضف رابط فروع الصيدليات
    if (userData.role === 'pharmacy') {
      baseMenuItems.push(
        {
          path: "/store-branches",
          icon: <FaStore style={{ fontSize: "20px", color: "#ffffff" }} />,
          text: "فروع الصيدليات"
        },
        {
          path: "/pharmacy-branch-requests",
          icon: <FaStore style={{ fontSize: "20px", color: "#ffffff" }} />,
          text: "طلبات الفروع"
        },
        {
          path: "/pharmacy-subcategories",
          icon: <FaTags style={{ fontSize: "20px", color: "#ffffff" }} />,
          text: "الفئات الفرعية"
        },
        {
          path: "/pharmacy-products",
          icon: <FaBox style={{ fontSize: "20px", color: "#ffffff" }} />,
          text: "المنتجات"
        },
        {
          path: "/main-branch/orders",
          icon: <FaShoppingCart style={{ fontSize: "20px", color: "#ffffff" }} />,
          text: "أوردرات الفروع"
        }
      );
    }

    // إذا كان المستخدم من نوع pharmacy_branch، أضف القائمة المناسبة للفرع
    if (userData.role === 'pharmacy_branch') {
      baseMenuItems.push(
        {
          path: "/pharmacy-products",
          icon: <FaBox style={{ fontSize: "20px", color: "#ffffff" }} />,
          text: "المنتجات"
        },
        {
          path: "/pharmacy-branch-requests-new",
          icon: <FaShoppingCart style={{ fontSize: "20px", color: "#ffffff" }} />,
          text: "طلبات الفرع"
        },
        {
          path: "/pharmacy-branch-orders",
          icon: <FaClipboardList style={{ fontSize: "20px", color: "#ffffff" }} />,
          text: "أوردرات الفرع"
        }
      );
    }

    // إذا كان المستخدم من نوع doctor، أضف قائمة الدكتور
    if (userData.role === 'doctor') {
      baseMenuItems.push(
        {
          path: "/doctor/invitations",
          icon: <FaEnvelope style={{ fontSize: "20px", color: "#ffffff" }} />,
          text: "الدعوات"
        },
        {
          path: "/doctor/clinics",
          icon: <FaHospital style={{ fontSize: "20px", color: "#ffffff" }} />,
          text: "العيادات"
        },
        {
          path: "/doctor/clinic-working-days",
          icon: <FaCalendarAlt style={{ fontSize: "20px", color: "#ffffff" }} />,
          text: "أيام العمل"
        },
        {
          path: "/doctor/reservations",
          icon: <FaCalendarAlt style={{ fontSize: "20px", color: "#ffffff" }} />,
          text: "الحجوزات"
        },
        {
          path: "/doctor/make-work-day",
          icon: <FaCalendarAlt style={{ fontSize: "20px", color: "#ffffff" }} />,
          text: "جدول العمل"
        },
        {
          path: "/doctor/day-slots",
          icon: <FaCalendarAlt style={{ fontSize: "20px", color: "#ffffff" }} />,
          text: "مواعيد العمل"
        },
        {
          path: "/doctor/consultation-price",
          icon: <FaDollarSign style={{ fontSize: "20px", color: "#ffffff" }} />,
          text: "سعر الكشف"
        }
      );
    }

    // إذا كان المستخدم من نوع clinic، أضف قائمة العيادة
    if (userData.role === 'clinic') {
      baseMenuItems.push(
        {
          path: "/clinic/branches",
          icon: <FaStore style={{ fontSize: "20px", color: "#ffffff" }} />,
          text: "فروع العيادة"
        },
        {
          path: "/clinic/doctors",
          icon: <FaStethoscope style={{ fontSize: "20px", color: "#ffffff" }} />,
          text: "الأطباء"
        }
      );
    }

    // إذا كان المستخدم من نوع admin، أضف الصلاحيات والمشرفين
    if (userData.role === 'admin') {
      // إشعارات الادمن
      baseMenuItems.push({
        path: "/admin/dashboard/notifications/send",
        icon: <FaEnvelope style={{ fontSize: "20px", color: "#ffffff" }} />, // يمكنك تغيير الأيقونة إذا أردت
        text: "ارسال الاشعارات"
      });
      // Roles and Admins - إضافة fallback للـ Super Admin
      const isSuperAdmin = userRoles.some(role => role.role_name_en === 'Super Admin');
      if (hasPermission('roles.index') || hasPermission('manage_admins') || isSuperAdmin) {
        baseMenuItems.push(
          {
            path: "/admin-permissions",
            icon: <FaShieldAlt style={{ fontSize: "20px", color: "#ffffff" }} />,
            text: "الصلاحيات" 
          },
          {
            path: "/admins",
            icon: <FaUsers style={{ fontSize: "20px", color: "#ffffff" }} />,
            text: "المشرفين" 
          }
        );
      }

      // Banners
      if (hasPermission('banners.index') || isSuperAdmin) {
        baseMenuItems.push({
          path: "/admin-banners",
          icon: <FaImages style={{ fontSize: "20px", color: "#ffffff" }} />,
          text: "البانرات" 
        });
      }

      // Clients
      if (hasPermission('clients.index') || isSuperAdmin) {
        baseMenuItems.push({
          path: "/admin-clients",
          icon: <FaUserFriends style={{ fontSize: "20px", color: "#ffffff" }} />,
          text: "المستخدمين" 
        });
      }

      // Stores
      if (hasPermission('stores.index') || isSuperAdmin) {
        baseMenuItems.push({
          path: "/admin-stores",
          icon: <FaStore style={{ fontSize: "20px", color: "#ffffff" }} />,
          text: "المتاجر" 
        });
      }

      // Pharmacies
      if (hasPermission('pharmacies.index') || isSuperAdmin) {
        baseMenuItems.push({
          path: "/admin-pharmacies",
          icon: <FaClinicMedical style={{ fontSize: "20px", color: "#ffffff" }} />,
          text: "الصيدليات" 
        });
      }

      // Pharmacy Settings
      if (hasPermission('pharmacies.settings.show') || hasPermission('pharmacy.settings.show') || isSuperAdmin) {
        baseMenuItems.push({
          path: "/admin-pharmacy-settings",
          icon: <FaCog style={{ fontSize: "20px", color: "#ffffff" }} />,
          text: "إعدادات الصيدليات" 
        });
      }

      // Products
      if (hasPermission('products.index') || isSuperAdmin) {
        baseMenuItems.push({
          path: "/admin-products",
          icon: <FaBox style={{ fontSize: "20px", color: "#ffffff" }} />,
          text: "المنتجات" 
        });
      }

      // Deliveries
      if (hasPermission('deliveries.index') || isSuperAdmin) {
        baseMenuItems.push({
          path: "/admin-deliveries",
          icon: <FaBiking style={{ fontSize: "20px", color: "#ffffff" }} />,
          text: "الديليفري" 
        });
      }

      // Pharmacy Requests
      if (hasPermission('requests.index') || isSuperAdmin) {
        baseMenuItems.push({
          path: "/admin-pharmacy-requests",
          icon: <FaFileAlt style={{ fontSize: "20px", color: "#ffffff" }} />,
          text: "طلبات الصيدليات" 
        });
      }

      // Orders
      if (hasPermission('orders.index') || isSuperAdmin) {
        baseMenuItems.push({
          path: "/admin/orders",
          icon: <FaShoppingCart style={{ fontSize: "20px", color: "#ffffff" }} />,
          text: "الأوردرات" 
        });
      }

      // Categories
      if (hasPermission('categories.index') || isSuperAdmin) {
        baseMenuItems.push({
          path: "/admin-categories",
          icon: <FaList style={{ fontSize: "20px", color: "#ffffff" }} />,
          text: "الفئات" 
        });
      }

      // Locations
      if (isSuperAdmin || hasPermission('countries.index') || true) {
        baseMenuItems.push(
          {
            path: "/admin-locations/countries",
            icon: <FaList style={{ fontSize: "20px", color: "#ffffff" }} />,
            text: "الدول" 
          },
          {
            path: "/admin-locations/governments",
            icon: <FaList style={{ fontSize: "20px", color: "#ffffff" }} />,
            text: "المحافظات" 
          },
          {
            path: "/admin-locations/centers",
            icon: <FaList style={{ fontSize: "20px", color: "#ffffff" }} />,
            text: "المراكز" 
          }
        );
      }

      // Clinics
      if (hasPermission('clinics.index') || isSuperAdmin) {
        baseMenuItems.push({
          path: "/admin-clinics",
          icon: <FaHospital style={{ fontSize: "20px", color: "#ffffff" }} />,
          text: "العيادات" 
        });
      }

      // Doctors
      if (hasPermission('doctors.index') || isSuperAdmin) {
        baseMenuItems.push({
          path: "/admin-doctors",
          icon: <FaStethoscope style={{ fontSize: "20px", color: "#ffffff" }} />,
          text: "الأطباء" 
        });
      }

      // Technicians
      if (hasPermission('technicans.index') || hasPermission('technicians.index') || isSuperAdmin) {
        baseMenuItems.push({
          path: "/admin-technicians",
          icon: <FaTools style={{ fontSize: "20px", color: "#ffffff" }} />,
          text: "الفنيين"
        });
      }

      // Units
      if (hasPermission('units.index') || isSuperAdmin) {
        baseMenuItems.push({
          path: "/admin-units",
          icon: <FaList style={{ fontSize: "20px", color: "#ffffff" }} />,
          text: "الوحدات"
        });
      }

      // Support
      if (hasPermission('support.index') || isSuperAdmin) {
        baseMenuItems.push({
          path: "/admin-support",
          icon: <FaHeadset style={{ fontSize: "20px", color: "#ffffff" }} />,
          text: "الدعم" 
        });
      }

      baseMenuItems.push({
        path: "/admin/branch-complaints",
        icon: <FaExclamationCircle style={{ fontSize: "20px", color: "#ffffff" }} />,
        text: "الشكاوي"
      });

      // Promo Codes
      if (hasPermission('promo_codes.index') || isSuperAdmin) {
        baseMenuItems.push({
          path: "/admin-promo-codes",
          icon: <FaTicketAlt style={{ fontSize: "20px", color: "#ffffff" }} />,
          text: "البرومو كود" 
        });
      }

      baseMenuItems.push({
        path: "/admin/settings",
        icon: <FaSlidersH style={{ fontSize: "20px", color: "#ffffff" }} />,
        text: "إعدادات التطبيق"
      });
    }

    // Store Promo Codes
    if (userData.role === 'store') {
      baseMenuItems.push({
        path: "/store-promo-codes",
        icon: <FaTicketAlt style={{ fontSize: "20px", color: "#ffffff" }} />,
        text: "البرومو كود"
      });
    }

    // Pharmacy Promo Codes
    if (userData.role === 'pharmacy') {
      baseMenuItems.push({
        path: "/pharmacy-promo-codes",
        icon: <FaTicketAlt style={{ fontSize: "20px", color: "#ffffff" }} />,
        text: "البرومو كود"
      });
    }

    return baseMenuItems;
  };

  const filteredMenuItems = getMenuItems();


  return (
    <ul className="mb-0  list-unstyled main-ul" style={{ gap: "0.5rem", marginTop: "20px" }}>
      {filteredMenuItems.map((item, index) => {
        const isActive = location.pathname === item.path;
        const iconColor = isActive ? "#184194" : "#6b7280";
        const IconComponent = item.icon.type;
        const iconProps = { ...item.icon.props, style: { ...item.icon.props.style, color: iconColor } };
        
        return (
          <li key={index} className={isActive ? 'active' : ''} style={{ marginBottom: "0.25rem", position: 'relative' }}>
            <div className="icon" style={{ marginRight: "0.25rem", display: "flex", alignItems: "center", justifyContent: "center", width: "25px", height: "25px" }}>
              <IconComponent {...iconProps} />
            </div>
            <Link to={item.path} onClick={safeHandleLinkClick}>
              <span>{item.text}</span>
            </Link>
          </li>
        );
      })}
    </ul>
  );
};

export default SideNavMenu;
